import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ACTIVE_USERS_CONFIG, GA4_AVERAGE_SESSION_CONFIG } from './ga4-overview-metrics.config';
import { combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { Ga4OverviewMetricsService } from './ga4-overview-metrics.service';
import { AnalyticsService } from '../../analytics/analytics.service';
import { DateRangeSelectorService } from '../../date-range-selector';
import { Router } from '@angular/router';
import { ChartSeries, fillInCurrentAverage, fillInCurrentTotal } from '../../metrics';
import { DisplayValueDescriptor } from '@vendasta/uikit';
import { TranslateService } from '@ngx-translate/core';
import { getImageSrc } from '../../../core/image-utilities';
import { convertSecondsToTimeString } from '../../../core/metrics.helper';

@Component({
  selector: 'app-ga4-overview-metrics',
  template: `
    @if ((loading$ | async) === false) {
      @if (isGaEnabled) {
        <div class="metric-card-holder">
          <app-metrics-card
            class="half-width-card"
            [header]="cardConfig.activeUsers.header"
            [currentPeriod]="activeUsersCurrentValue$ | async"
            [hideTopStats]="ga4ViewNotFound$ | async"
            [currentAgeText]="ageText"
            >
            <ng-template [ngTemplateOutlet]="edgeCases"></ng-template>
            <div chart-legend>
              <mat-card-actions>
                <mat-divider></mat-divider>
                <button class="footer" mat-button (click)="viewMore('active-users')">
                  {{ 'ANALYTICS.ANALYTICS_PANEL.BUTTONS.VIEW_MORE' | translate }}
                </button>
              </mat-card-actions>
            </div>
          </app-metrics-card>
          <app-metrics-card
            class="half-width-card"
            [header]="cardConfig.averageSession.header"
            [formatValueDisplayFunc]="convertSecondsToHHMMSS"
            [currentPeriod]="sessionDurationCurrentValue$ | async"
            [hideTopStats]="ga4ViewNotFound$ | async"
            [currentAgeText]="ageText"
            >
            <ng-template [ngTemplateOutlet]="edgeCases"></ng-template>
            <div chart-legend>
              <mat-card-actions>
                <mat-divider></mat-divider>
                <button class="footer" mat-button (click)="viewMore('average-session-duration')">
                  {{ 'ANALYTICS.ANALYTICS_PANEL.BUTTONS.VIEW_MORE' | translate }}
                </button>
              </mat-card-actions>
            </div>
          </app-metrics-card>
        </div>
      }
    } @else {
      @if (!isGaEnabled) {
        <div class="metric-card-holder">
          <div class="loading-state">
            <app-loading-state-card
              class="loading-state"
              [header]="cardConfig.activeUsers.header"
            ></app-loading-state-card>
          </div>
          <div class="loading-state">
            <app-loading-state-card [header]="cardConfig.averageSession.header"></app-loading-state-card>
          </div>
        </div>
      }
    }
    
    <ng-template #edgeCases>
      @if (error$ | async) {
        <div class="api-error-container">{{ 'ANALYTICS.FAILED_GET_DATA' | translate }}</div>
      } @else {
        @if (ga4ViewNotFound$ | async) {
          <div class="connect-ga">
            <img src="{{ addChartImage }}" alt="" />
            <br />
            {{ 'ANALYTICS.ANALYTICS_PANEL.GATHER_SITE_INSIGHT' | translate }}
            <br />
            @if ((connectGALinkLoading$ | async) === false) {
              @if ((connectGALinkError$ | async) === false) {
                <a href="{{ connectGALink$ | async }}" class="connect-google-analytics-button" target="_blank">
                  {{ 'ANALYTICS.ANALYTICS_PANEL.CONNECT_GA_VIEW_CTA' | translate }}
                </a>
              }
            }
          </div>
        }
      }
    </ng-template>
    
    `,
  styleUrls: ['./ga4-overview-metrics.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class Ga4OverviewMetricsComponent implements OnInit {
  @Input() siteId: string;

  readonly cardConfig = {
    activeUsers: ACTIVE_USERS_CONFIG,
    averageSession: GA4_AVERAGE_SESSION_CONFIG,
  };
  ageText: string;
  addChartImage = getImageSrc('images/add-chart.svg');

  loading$: Observable<boolean>;
  error$: Observable<boolean>;
  isGaEnabled = false;

  activeUsersCurrentValue$: Observable<DisplayValueDescriptor>;
  sessionDurationCurrentValue$: Observable<DisplayValueDescriptor>;

  ga4ViewNotFound$: Observable<boolean>;
  ga4ViewError$: Observable<boolean>;
  connectGALinkLoading$: Observable<boolean>;
  connectGALinkError$: Observable<boolean>;
  connectGALink$: Observable<string>;

  constructor(
    private ga4OverviewMetricsService: Ga4OverviewMetricsService,
    private snowplow: ProductAnalyticsService,
    private analyticsService: AnalyticsService,
    private dateRangeSelectorService: DateRangeSelectorService,
    private translateService: TranslateService,
    private router: Router,
  ) {
    this.ageText = this.translateService.instant('ANALYTICS.DATE_RANGE_SELECTOR.TIME_PERIOD.LAST_7_DAYS');
    this.connectGALink$ = this.analyticsService.connectGALink$;
    this.connectGALinkError$ = this.analyticsService.connectGALinkError$;
    this.connectGALinkLoading$ = this.analyticsService.connectGALinkLoading$;
    this.ga4ViewNotFound$ = this.analyticsService.ga4ViewNotFound$;

    this.loading$ = combineLatest([
      this.ga4OverviewMetricsService.loadingGA4Records$,
      this.analyticsService.gaViewLoading$,
    ]).pipe(map(([recordsLoading, viewLoading]) => recordsLoading || viewLoading));
    this.error$ = combineLatest([
      this.ga4OverviewMetricsService.loadingGA4RecordsError$,
      this.analyticsService.gaViewError$,
    ]).pipe(map(([recordsErr, viewErr]) => recordsErr || viewErr));
    this.activeUsersCurrentValue$ = fillInCurrentTotal(
      this.ga4OverviewMetricsService.ga4Records$.pipe(
        filter((records) => !!records),
        map(([current]) => {
          return [
            {
              name: 'activeUsers',
              series: current.activeUsers,
            } as ChartSeries,
          ];
        }),
      ),
    );
    this.sessionDurationCurrentValue$ = fillInCurrentAverage(
      this.ga4OverviewMetricsService.ga4Records$.pipe(
        filter((records) => !!records),
        map(([current]) => {
          return [
            {
              name: 'Average Session Duration',
              series: current.averageSession,
            } as ChartSeries,
          ];
        }),
      ),
    );
  }

  ngOnInit(): void {
    this.analyticsService.isGaEnabled.subscribe((data) => {
      this.isGaEnabled = data;
    });
    this.analyticsService.getGaIsEnabledState(this.siteId).subscribe();
  }

  /**
   * Takes users to navigate to analysis tab
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public viewMore(graphName: string): void {
    this.dateRangeSelectorService.setTimeRange('7days');
    const url = `/site/${this.siteId}/analytics`;
    this.router.navigateByUrl(url);
  }

  convertSecondsToHHMMSS(sec: DisplayValueDescriptor): DisplayValueDescriptor {
    sec.value = convertSecondsToTimeString(sec.value as number);
    sec.displayType = 'string';
    return sec;
  }
}
